import { useMarketplacePermissions } from '@anytag/auth';
import { useFocusTrapReset, useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { Button, Form, RenderDataWithFallback, Drawer, BaseModal } from '@anytag/shared-atoms';
import {
  namedOperations,
  useMarketplaceDraftPostQuery,
  useMarketplacePostHistoryQuery,
  MarketplaceCampaignDraftPostStatus,
  useRejectMarketplaceDraftPostMutation,
  useApproveMarketplaceDraftPostMutation
} from '@anytag/graphql';
import { HistorySection, PostInfoSection, RequestModificationButton, RequestModificationMessage } from './shared';

interface DraftTemplateProps {
  postId: number;
  onClose?: () => void;
}

export const DraftTemplateComponent = ({ postId, onClose }: DraftTemplateProps) => {
  const confirmModal = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideApproveRejectInappropriatePostBtn } = useMarketplacePermissions();
  const { data, loading } = useMarketplaceDraftPostQuery({
    variables: { draftId: postId },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });
  const { data: historyData, loading: fetchingHistory } = useMarketplacePostHistoryQuery({
    variables: { draftId: postId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });
  const { callApproveMarketplaceDraftPost, loading: approving } = useApproveMarketplaceDraftPostMutation({
    refetchQueries: [namedOperations.Query.AllMarketplacePosts]
  });
  const { callRejectMarketplaceDraftPost, loading: rejecting } = useRejectMarketplaceDraftPostMutation({
    refetchQueries: [
      namedOperations.Query.AllMarketplacePosts,
      namedOperations.Query.MarketplaceDraftPost,
      namedOperations.Query.MarketplacePostHistory
    ]
  });

  useFocusTrapReset();

  const marketplaceDraftPost = data?.marketplaceDraftPost;

  if (!marketplaceDraftPost) {
    return null;
  }

  const { id, status, rejectedDate, rejectedMessage, createdDate, caption, images, influencer } = marketplaceDraftPost;
  const isRejected = status === MarketplaceCampaignDraftPostStatus.REJECTED;
  const hasRequestModificationButton =
    !hideApproveRejectInappropriatePostBtn && status === MarketplaceCampaignDraftPostStatus.DRAFT;
  const hasApproveButton =
    !hideApproveRejectInappropriatePostBtn && status !== MarketplaceCampaignDraftPostStatus.APPROVED;

  const handleSendModification = async (message: string) => {
    try {
      await callRejectMarketplaceDraftPost({ variables: { input: { draftId: id, message } } });
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  const handleApproveDraftPost = async () => {
    try {
      await callApproveMarketplaceDraftPost({ variables: { input: { draftId: id } } });
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
      onClose?.();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <Drawer.Body>
      <RenderDataWithFallback loading={loading || fetchingHistory}>
        <div className="pb-6">
          <PostInfoSection
            hasImageDownload
            caption={caption}
            postedDate={createdDate}
            images={[...(images || [])]}
            influencer={{
              id: influencer.id,
              name: influencer.name,
              avatar: influencer.avatar,
              followers: influencer.followersCount
            }}
          />

          {hasRequestModificationButton ? (
            <RequestModificationButton loading={approving || rejecting} onSubmit={handleSendModification} />
          ) : null}

          {isRejected ? <RequestModificationMessage date={rejectedDate || ''} message={rejectedMessage} /> : null}
        </div>

        <HistorySection data={historyData?.marketplacePostHistory} isDraftPost />

        {hasApproveButton ? (
          <Form.Footer className="sticky bottom-0 z-5 justify-between bg-white">
            <p className="max-w-51 text-sm text-tertiary">
              {t('You can send feedback to creator via Email and AnyCreator')}
            </p>
            <Button
              variant="solid"
              disabled={isRejected}
              onClick={confirmModal.open}
              loading={approving || rejecting}
              title={t('Button.Approve draft')}
              className="min-w-[unset] px-8"
              tooltip={{
                disabled: !isRejected,
                help: t('Tooltip.You cannot approve the draft post until the creator submits a new draft post')
              }}
            />
          </Form.Footer>
        ) : null}

        <BaseModal
          open={confirmModal.status}
          onClose={confirmModal.close}
          title={t('Approve draft post')}
          description={t('Are you sure you want to approve this draft post?')}
          footer={{
            cancelButton: { onClick: confirmModal.close },
            submitButton: {
              title: t('Button.Approve'),
              onClick: handleApproveDraftPost,
              loading: { showIcon: true, status: approving }
            }
          }}
        />
      </RenderDataWithFallback>
    </Drawer.Body>
  );
};
